import React, { useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { useT } from "@/i18n";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Todas las fotos de la obra (avances y recibos), filtrables y descargables individualmente o en zip. */
export const Gallery = ({ projectId, code, avances = [], movimientos = [] }) => {
  const { t } = useT();
  const [filter, setFilter] = useState("all");
  const [viewer, setViewer] = useState(null);

  const items = useMemo(() => {
    const fromAvances = avances.flatMap((a) =>
      (a.fotos || []).map((f) => ({ ...f, tipo: "avance", fecha: a.fecha, label: a.descripcion })));
    const fromRecibos = movimientos.filter((m) => m.recibo).map((m) => ({
      ...m.recibo, tipo: "recibo", fecha: m.fecha, label: m.descripcion || m.proveedor,
    }));
    return [...fromAvances, ...fromRecibos].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  }, [avances, movimientos]);

  const filtered = filter === "all" ? items : items.filter((i) => i.tipo === (filter === "progress" ? "avance" : "recibo"));

  const downloadZip = async () => {
    const res = await api.get(`/projects/${projectId}/gallery.zip`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${code}_galeria.zip`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!items.length) {
    return <p className="py-6 text-center text-[12.5px] text-ink-tertiary" data-testid="gallery-empty">{t("projects.gallery.empty")}</p>;
  }

  return (
    <div data-testid="project-gallery">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {["all", "progress", "receipts"].map((f) => (
            <button key={f} data-testid={`gallery-filter-${f}`} onClick={() => setFilter(f)}
              className={cn(
                "border px-2.5 py-1 text-[11.5px] font-medium transition-colors",
                filter === f ? "border-brand bg-brand-soft text-ink" : "border-line text-ink-secondary hover:border-line-strong"
              )}>
              {t(`projects.gallery.filter_${f}`)}
            </button>
          ))}
        </div>
        <button data-testid="gallery-download-zip" onClick={downloadZip}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-brand hover:text-brand-hover">
          <Download className="h-3.5 w-3.5" /> {t("projects.gallery.download_zip")}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {filtered.map((f, idx) => (
          <figure key={`${f.key}-${idx}`} className="group relative">
            <button data-testid={`gallery-photo-${idx}`} onClick={() => setViewer(f)}
              className="block w-full overflow-hidden border border-line">
              <img src={f.thumb_url || f.url} alt={f.tipo}
                className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
            </button>
            <figcaption className="absolute left-1 top-1 px-1.5 py-[2px] font-mono text-[9.5px] uppercase tracking-wide text-paper"
              style={{ backgroundColor: "var(--ink-overlay)" }}>
              {t(`projects.gallery.filter_${f.tipo === "avance" ? "progress" : "receipts"}`)}
            </figcaption>
          </figure>
        ))}
      </div>

      {viewer && (
        <div data-testid="gallery-viewer" className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 p-6"
          onClick={() => setViewer(null)}>
          <button data-testid="gallery-viewer-close" className="absolute right-5 top-5 text-white/80 hover:text-white" onClick={() => setViewer(null)}>
            <X className="h-6 w-6" />
          </button>
          <img src={viewer.url} alt="" className="max-h-[80vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="mt-3 flex items-center gap-4 text-[12.5px] text-white/80" onClick={(e) => e.stopPropagation()}>
            <span>{formatDate(viewer.fecha)}</span>
            {viewer.label && <span>{viewer.label}</span>}
            <a href={viewer.url} download className="inline-flex items-center gap-1 text-white hover:underline">
              <Download className="h-3.5 w-3.5" /> {t("app.download")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
